import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import BillboardListingItem from '../components/BillboardListingItem';
import { FaFire } from 'react-icons/fa';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';

export default function AweitBillboard() {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);

    // Format price helper function
    const formatPrice = (price) => {
        if (price === 'Negotiable') return 'Negotiable';
        if (price == null) return 'N/A';
        
        if (typeof price === 'number') {
            return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return price?.toString() || 'N/A';
    };

    // Get price text helper function
    const getPriceText = (listing) => {
        if (!listing) return 'N/A';
        
        const price = listing.price;
        const currency = listing.currency || '';

        if (price === 'Negotiable') return 'Negotiable';
        if (price == null) return 'N/A';

        return `${currency} ${formatPrice(price)}`.trim();
    };

    // Fetch listings sorted by viewCount
    useEffect(() => {
        async function fetchListings() {
            try {
                setLoading(true);
                
                const listingsRef = collection(db, 'listings');
                const q = query(listingsRef, orderBy('viewCount', 'desc'), limit(10));
                const querySnap = await getDocs(q);
                
                const listingsData = querySnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setListings(listingsData);
            } catch (error) {
                console.error("Error fetching listings:", error);
                toast.error("Could not fetch listings. Please try again.");
            } finally {
                setLoading(false);
            }
        }
        
        fetchListings();
    }, []);

    if (loading) {
        return <Spinner />;
    }

    return (
        <div className="min-h-screen bg-[#1a1a2e] py-12 px-4 overflow-x-hidden">
            {/* Billboard Background with 3D Effect */}
            <div className="relative max-w-4xl mx-auto">
                {/* 3D Pin Board Background */}
                <div 
                    className="absolute inset-0 bg-[#16213e] rounded-xl shadow-2xl transform rotate-1 -z-10"
                    style={{
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.6), 0 10px 10px -5px rgba(0, 0, 0, 0.4)'
                    }}
                >
                    {/* Subtle pattern overlay */}
                    <div 
                        className="absolute inset-0 rounded-xl opacity-30"
                        style={{
                            backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.05) 0%, transparent 20%)'
                        }}
                    ></div>
                </div>

                {/* Main Content with 3D Transform */}
                <div 
                    className="relative bg-[#16213e] bg-opacity-80 rounded-xl p-4 sm:p-8 shadow-lg transform -rotate-1 backdrop-blur-sm w-full overflow-hidden"
                    style={{
                        transform: 'perspective(1000px) rotateX(2deg)',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.6), 0 10px 10px -5px rgba(0, 0, 0, 0.4)'
                    }}
                >
                    {/* Main Title with Gradient - Responsive text size */}
                    <h1 
                        className="text-2xl sm:text-3xl md:text-4xl font-black text-center mb-2 truncate max-w-full px-2"
                        style={{
                            background: 'linear-gradient(45deg, #FF69B4, #00B4D8)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                            transform: 'perspective(500px) rotateX(-2deg)',
                            letterSpacing: '1px sm:letter-spacing-2px'
                        }}
                    >
                        SOURCE BILLBOARD
                    </h1>

                    {/* Subtitle with Fire Icon */}
                    <div className="flex items-center justify-center space-x-2 mb-6 sm:mb-8">
                        <FaFire className="text-orange-500 text-xl sm:text-2xl md:text-3xl animate-pulse flex-shrink-0" />
                        <h2 
                            className="text-sm sm:text-base md:text-xl font-light text-white text-opacity-70 truncate max-w-full"
                            style={{
                                transform: 'perspective(500px) rotateX(-1deg)',
                                letterSpacing: '0.8px sm:letter-spacing-1.2px'
                            }}
                        >
                            Today's Top Listings
                        </h2>
                    </div>

                    {/* Listing List - Ensure container doesn't overflow */}
                    <div className="space-y-4 w-full overflow-hidden">
                        {listings && listings.length > 0 ? (
                            listings.map((listing, index) => (
                                <div key={listing.id} className="w-full overflow-hidden">
                                    <BillboardListingItem
                                        listing={listing}
                                        rank={index + 1}
                                        getPriceText={getPriceText}
                                    />
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-white text-opacity-70 text-base sm:text-lg mb-2">
                                    No listings available yet
                                </p>
                                <p className="text-white text-opacity-50 text-xs sm:text-sm">
                                    Check back later for trending items
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Pin Effects - Hidden on mobile to prevent overflow */}
                <div className="hidden sm:block absolute top-0 left-0 w-8 h-8 bg-yellow-500 rounded-full -mt-4 -ml-4 shadow-lg transform rotate-12"></div>
                <div className="hidden sm:block absolute top-0 right-0 w-8 h-8 bg-gray-400 rounded-full -mt-4 -mr-4 shadow-lg transform -rotate-12"></div>
                <div className="hidden sm:block absolute bottom-0 left-0 w-8 h-8 bg-amber-700 rounded-full -mb-4 -ml-4 shadow-lg transform -rotate-12"></div>
                <div className="hidden sm:block absolute bottom-0 right-0 w-8 h-8 bg-blue-400 rounded-full -mb-4 -mr-4 shadow-lg transform rotate-12"></div>
            </div>

            {/* Inject global styles to ensure no horizontal overflow */}
            <style jsx>{`
                /* Ensure no horizontal scroll on mobile */
                body {
                    overflow-x: hidden;
                }
                .movie-masonry {
                    overflow-x: hidden;
                }
                /* Force all text to wrap/truncate properly */
                .truncate {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    max-width: 100%;
                }
            `}</style>
        </div>
    );
}